/**
 * Utility functions for formatting data
 */

/**
 * Format currency values
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @param {string} locale - Locale for formatting (default: en-US)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0.00';
  }
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format numbers with thousands separators
 * @param {number} num - Number to format
 * @param {string} locale - Locale for formatting (default: en-US)
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, locale = 'en-US') => {
  if (typeof num !== 'number' || isNaN(num)) {
    return '0';
  }
  
  return new Intl.NumberFormat(locale).format(num);
};

/**
 * Format dates
 * @param {Date|string} date - Date to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(dateObj);
};

/**
 * Format date and time
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateObj);
  }
};

/**
 * Format file sizes
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted file size string
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format percentage
 * @param {number} value - Value to format as percentage (0.5 = 50%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0%';
  }
  
  return (value * 100).toFixed(decimals) + '%';
};

/**
 * Format phone numbers (US format)
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }
  
  return phone; // Return original if can't format
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || typeof text !== 'string') return '';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Format weight (assumes input in pounds)
 * @param {number} weight - Weight in pounds
 * @param {string} unit - Unit to display (default: 'lbs')
 * @returns {string} Formatted weight string
 */
export const formatWeight = (weight, unit = 'lbs') => {
  if (typeof weight !== 'number' || isNaN(weight)) {
    return `0 ${unit}`;
  }
  
  return `${formatNumber(weight)} ${unit}`;
};

/**
 * Format address
 * @param {object} address - Address object with street, city, state, zip
 * @returns {string} Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return '';
  
  const parts = [];
  
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zip) parts.push(address.zip);
  
  return parts.join(', ');
};

/**
 * Format product quantity display
 * @param {number} caseQuantity - Number of cases
 * @param {number} unitQuantity - Number of individual units
 * @param {number} unitsPerCase - Units per case
 * @returns {string} Formatted quantity string
 */
export const formatQuantity = (caseQuantity = 0, unitQuantity = 0, unitsPerCase = 1) => {
  const totalUnits = (caseQuantity * unitsPerCase) + unitQuantity;
  const parts = [];
  
  if (caseQuantity > 0) {
    parts.push(`${caseQuantity} case${caseQuantity !== 1 ? 's' : ''}`);
  }
  
  if (unitQuantity > 0) {
    parts.push(`${unitQuantity} unit${unitQuantity !== 1 ? 's' : ''}`);
  }
  
  const result = parts.join(' + ');
  
  if (totalUnits > 0) {
    return `${result} (${formatNumber(totalUnits)} total units)`;
  }
  
  return '0 items';
};

/**
 * Format quantity display for cart items (cases + units breakdown)
 * @param {number} totalUnits - Total unit quantity
 * @param {number} unitsPerCase - Units per case
 * @returns {object} Breakdown of cases and units
 */
export const formatCartQuantity = (totalUnits, unitsPerCase = 1) => {
  if (!totalUnits || totalUnits === 0) {
    return {
      cases: 0,
      units: 0,
      displayText: '0 units',
      shortText: '0'
    };
  }

  if (unitsPerCase <= 1) {
    return {
      cases: 0,
      units: totalUnits,
      displayText: `${totalUnits} unit${totalUnits === 1 ? '' : 's'}`,
      shortText: `${totalUnits}u`
    };
  }

  const cases = Math.floor(totalUnits / unitsPerCase);
  const remainingUnits = totalUnits % unitsPerCase;

  let displayText = '';
  let shortText = '';

  if (cases === 0) {
    displayText = `${remainingUnits} unit${remainingUnits === 1 ? '' : 's'}`;
    shortText = `${remainingUnits}u`;
  } else if (remainingUnits === 0) {
    displayText = `${cases} case${cases === 1 ? '' : 's'}`;
    shortText = `${cases}c`;
  } else {
    displayText = `${cases} case${cases === 1 ? '' : 's'}, ${remainingUnits} unit${remainingUnits === 1 ? '' : 's'}`;
    shortText = `${cases}c+${remainingUnits}u`;
  }

  return {
    cases,
    units: remainingUnits,
    displayText,
    shortText
  };
};

/**
 * Format case price display with unit breakdown
 * @param {number} unitPrice - Price per unit
 * @param {number} unitsPerCase - Units per case
 * @returns {string} Formatted case price
 */
export const formatCasePrice = (unitPrice, unitsPerCase) => {
  const casePrice = unitPrice * unitsPerCase;
  return `${formatCurrency(casePrice)} per case (${unitsPerCase} units)`;
};

/**
 * Format unit price with optional single unit fee
 * @param {number} unitPrice - Base unit price
 * @param {number} singleUnitFee - Additional fee for single units
 * @param {boolean} feeApplies - Whether the fee applies
 * @returns {string} Formatted unit price
 */
export const formatUnitPriceWithFee = (unitPrice, singleUnitFee = 0, feeApplies = false) => {
  const basePrice = formatCurrency(unitPrice);
  
  if (!feeApplies || singleUnitFee === 0) {
    return `${basePrice} per unit`;
  }
  
  const totalPrice = unitPrice + singleUnitFee;
  const feeText = formatCurrency(singleUnitFee);
  
  return `${formatCurrency(totalPrice)} per unit (incl. ${feeText} fee)`;
};

/**
 * Format cart item total with fee breakdown
 * @param {number} unitQuantity - Total units
 * @param {number} unitPrice - Price per unit
 * @param {number} singleUnitFee - Single unit fee
 * @param {number} unitsPerCase - Units per case
 * @returns {object} Price breakdown
 */
export const formatCartItemTotal = (unitQuantity, unitPrice, singleUnitFee = 0, unitsPerCase = 1) => {
  const subtotal = unitQuantity * unitPrice;
  const feeApplies = unitQuantity < unitsPerCase;
  const totalFees = feeApplies ? unitQuantity * singleUnitFee : 0;
  const total = subtotal + totalFees;

  return {
    subtotal: formatCurrency(subtotal),
    fees: formatCurrency(totalFees),
    total: formatCurrency(total),
    feeApplies,
    rawSubtotal: subtotal,
    rawFees: totalFees,
    rawTotal: total
  };
};

/**
 * Format stock status with appropriate styling classes
 * @param {number} unitAvailable - Available units
 * @param {number} alertLevel - Low stock threshold
 * @returns {object} Stock status info
 */
export const formatStockStatus = (unitAvailable, alertLevel = 10) => {
  if (!unitAvailable || unitAvailable === 0) {
    return {
      status: 'out-of-stock',
      text: 'Out of Stock',
      className: 'text-danger',
      badgeClass: 'bg-danger',
      available: 0
    };
  }

  if (unitAvailable <= alertLevel) {
    return {
      status: 'low-stock',
      text: `Low Stock (${unitAvailable} available)`,
      className: 'text-warning',
      badgeClass: 'bg-warning',
      available: unitAvailable
    };
  }

  return {
    status: 'in-stock',
    text: `${unitAvailable} available`,
    className: 'text-success',
    badgeClass: 'bg-success',
    available: unitAvailable
  };
};

/**
 * Format cart summary for display
 * @param {array} cartItems - Array of cart items
 * @returns {object} Cart summary data
 */
export const formatCartSummary = (cartItems = []) => {
  const summary = cartItems.reduce((acc, item) => {
    const itemTotal = formatCartItemTotal(
      item.UnitQuantity || 0,
      item.UnitPrice || 0,
      parseFloat(item.SingleUnitFee || 0),
      item.ProductSkuVariant?.UnitsPerCase || 1
    );

    acc.subtotal += itemTotal.rawSubtotal;
    acc.fees += itemTotal.rawFees;
    acc.total += itemTotal.rawTotal;
    acc.itemCount += 1;
    acc.unitCount += (item.UnitQuantity || 0);

    return acc;
  }, {
    subtotal: 0,
    fees: 0,
    total: 0,
    itemCount: 0,
    unitCount: 0
  });

  return {
    ...summary,
    formattedSubtotal: formatCurrency(summary.subtotal),
    formattedFees: formatCurrency(summary.fees),
    formattedTotal: formatCurrency(summary.total),
    itemText: `${summary.itemCount} item${summary.itemCount !== 1 ? 's' : ''}`,
    unitText: `${summary.unitCount} unit${summary.unitCount !== 1 ? 's' : ''}`
  };
};
// Export all functions as default object for easy importing
export default {
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatFileSize,
  formatPercentage,
  formatPhoneNumber,
  truncateText,
  formatWeight,
  formatAddress,
  formatQuantity,
  formatCartSummary,
  formatStockStatus,
  formatCartItemTotal,
  formatUnitPriceWithFee,
  formatCasePrice,
  formatCartQuantity
};